import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2, Mail, Phone, User, ArrowLeft, CheckCircle2, Loader2,
  Search, Briefcase, Plus, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentRequestAccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // null | 'linked' | 'awaits_company'
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const [companyNotListed, setCompanyNotListed] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', mobile: '',
    manager_name: '', manager_email: '',
    company_id: '', company_name: '',
    new_company_name: '',
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/agent-requests/companies`);
        const data = await res.json();
        if (res.ok) {
          setCompanies(data.companies || []);
          const presetId = params.get('company');
          if (presetId) {
            const match = (data.companies || []).find(c => c.company_id === presetId);
            if (match) {
              setForm(f => ({ ...f, company_id: match.company_id, company_name: match.company_name }));
              setQuery(match.company_name);
              setPrefilled(true);
            }
          }
        }
      } catch { /* ignore */ }
      setLoadingCompanies(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  // Click-outside + Esc to close dropdown
  useEffect(() => {
    const onDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  const filteredCompanies = useMemo(() => {
    const q = debouncedQuery.toLowerCase().trim();
    if (!q) return companies;
    return companies.filter(c => (c.company_name || '').toLowerCase().includes(q));
  }, [companies, debouncedQuery]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickCompany = (c) => {
    setForm({ ...form, company_id: c.company_id, company_name: c.company_name });
    setQuery(c.company_name);
    setDropdownOpen(false);
  };

  const clearSearch = () => {
    setQuery('');
    setForm({ ...form, company_id: '', company_name: '' });
    setPrefilled(false);
    setDropdownOpen(true);
    searchInputRef.current?.focus();
  };

  const switchToNotListed = () => {
    setCompanyNotListed(true);
    setDropdownOpen(false);
    setForm(f => ({ ...f, company_id: '', company_name: '', new_company_name: query }));
  };

  const switchToListed = () => {
    setCompanyNotListed(false);
    setForm(f => ({ ...f, new_company_name: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error('Please complete your name and email'); return;
    }
    if (!form.manager_name || !form.manager_email) {
      toast.error("Please provide your manager's name and email"); return;
    }
    if ((form.manager_email || '').toLowerCase().trim() === (form.email || '').toLowerCase().trim()) {
      toast.error("Your manager's email must be different from your own"); return;
    }
    if (companyNotListed) {
      if (!form.new_company_name.trim()) {
        toast.error("Please enter your company's name"); return;
      }
    } else if (!form.company_id) {
      toast.error('Please select a management company'); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        mobile: form.mobile || null,
        manager_name: form.manager_name,
        manager_email: form.manager_email,
        company_not_listed: companyNotListed,
        company_id: companyNotListed ? null : form.company_id,
        new_company_name: companyNotListed ? form.new_company_name : null,
      };
      const res = await fetch(`${API_URL}/api/agent-requests/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.awaits_company_registration ? 'awaits_company' : 'linked');
        toast.success('Request submitted!');
      } else {
        toast.error(data.detail || 'Submission failed');
      }
    } catch { toast.error('Network error — please retry'); }
    finally { setSubmitting(false); }
  };

  if (success) {
    const awaitsCompany = success === 'awaits_company';
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg" data-testid="request-success-card">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Request Submitted</h2>
            {awaitsCompany ? (
              <>
                <p className="text-slate-600 mb-4">
                  We've emailed <strong>{form.manager_name}</strong> (<span className="text-indigo-600">{form.manager_email}</span>) with instructions to register <strong>{form.new_company_name}</strong> on BodyCorp365.
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  Once they complete registration, your request will be automatically linked and you'll be notified at <span className="text-indigo-600">{form.email}</span>.
                </p>
              </>
            ) : (
              <>
                <p className="text-slate-600 mb-4">
                  Your access request has been sent to <strong>{form.manager_name || 'the Super Agent'}</strong> at <strong>{form.company_name}</strong>.
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  You'll receive an email at <span className="text-indigo-600 font-medium">{form.email}</span> once your request has been actioned.
                </p>
              </>
            )}
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="goto-login-btn">Go to Login</Button>
              <Button variant="outline" onClick={() => navigate('/')} className="border-slate-300">Back to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50">
      <header className="p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-indigo-600">
            <ArrowLeft className="w-5 h-5" /><span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">BodyCorp365</span>
          </div>
        </div>
      </header>

      <main className="flex justify-center p-4 pb-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Request Agent Access</h1>
            <p className="text-slate-600">Join an existing management company on BodyCorp365</p>
          </div>

          <Card className="bg-white border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-slate-800">Agent Details</CardTitle>
              <CardDescription>
                Your Super Agent will review the request. On approval, you'll receive login credentials by email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {prefilled && form.company_id && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2" data-testid="prefilled-banner">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">You're joining <strong>{form.company_name}</strong></p>
                    <p className="text-xs text-emerald-700">Pre-selected from your invite link.</p>
                  </div>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input value={form.full_name} onChange={set('full_name')} className="pl-10" placeholder="Jane Doe" required data-testid="agent-name-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input type="email" value={form.email} onChange={set('email')} className="pl-10" placeholder="jane@example.com" required data-testid="agent-email-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input value={form.mobile} onChange={set('mobile')} className="pl-10" placeholder="+27 82 123 4567 (optional)" data-testid="agent-mobile-input" />
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your Manager (Super Agent)</p>
                  <div className="space-y-2">
                    <Label>Manager's Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input value={form.manager_name} onChange={set('manager_name')} className="pl-10" placeholder="Sam Smith" required data-testid="manager-name-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Manager's Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input type="email" value={form.manager_email} onChange={set('manager_email')} className="pl-10" placeholder="sam@yourcompany.com" required data-testid="manager-email-input" />
                    </div>
                    <p className="text-xs text-slate-500">Approval request will be emailed directly to your manager.</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Management Company *</p>

                  {!companyNotListed ? (
                    <div className="relative" ref={dropdownRef}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
                      <Input
                        ref={searchInputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); setForm({ ...form, company_id: '', company_name: '' }); setPrefilled(false); }}
                        onFocus={() => setDropdownOpen(true)}
                        className="pl-10 pr-9"
                        placeholder={loadingCompanies ? 'Loading…' : 'Search and select your company'}
                        data-testid="company-search-input"
                      />
                      {query && (
                        <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-500 hover:bg-slate-100" aria-label="Clear search" data-testid="company-clear-btn">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      {dropdownOpen && filteredCompanies.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="company-dropdown">
                          {filteredCompanies.map((c) => (
                            <button
                              type="button"
                              key={c.company_id}
                              onClick={() => pickCompany(c)}
                              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b last:border-0 text-slate-700"
                              data-testid={`company-option-${c.company_id}`}
                            >
                              <div className="font-medium">{c.company_name}</div>
                              <div className="text-xs text-slate-500">{c.country}{c.province ? ` · ${c.province}` : ''}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {dropdownOpen && !loadingCompanies && filteredCompanies.length === 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-sm" data-testid="company-no-results">
                          <p className="text-slate-700 mb-3">No active management companies match "{query}".</p>
                          <div className="flex gap-2">
                            <Button type="button" size="sm" onClick={switchToNotListed} className="bg-indigo-600 hover:bg-indigo-700" data-testid="not-listed-btn">
                              <Plus className="w-4 h-4 mr-1" /> My company isn't listed
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={clearSearch} className="border-slate-300">
                              Clear search
                            </Button>
                          </div>
                        </div>
                      )}
                      {form.company_id && (
                        <p className="text-xs text-emerald-600 mt-1" data-testid="company-selected-indicator">✓ Selected: {form.company_name}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        Can't find your company?{' '}
                        <button type="button" onClick={switchToNotListed} className="text-indigo-600 hover:underline font-medium" data-testid="open-not-listed-link">
                          My company isn't listed
                        </button>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-800">
                        We'll email your manager an invitation to register <strong>{form.new_company_name || 'your company'}</strong> on BodyCorp365. Once they complete registration, your request will be linked automatically.
                      </div>
                      <Label>Your Company's Name *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input value={form.new_company_name} onChange={set('new_company_name')} className="pl-10" placeholder="PropManage (Pty) Ltd" required data-testid="new-company-name-input" />
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={switchToListed} className="border-slate-300 text-slate-600 hover:bg-slate-50" data-testid="back-to-listed-btn">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back to search
                      </Button>
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-12" data-testid="submit-request-btn">
                  {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</> : 'Request Access'}
                </Button>

                <div className="text-center text-sm text-slate-600 pt-2 space-y-1">
                  <p>Are you a Management Company? <Link to="/company-signup" className="text-indigo-600 font-medium hover:underline">Register your company instead</Link>.</p>
                  <p>Already have an account? <Link to="/login" className="text-indigo-600 font-medium hover:underline">Log in</Link>.</p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
